import { useParams } from 'wouter';
import { ROUTES } from '../routes/consts';
import cx from 'classix';
import { CreateProjectContext, SkillType, SupportType } from './consts';
import { Button, Icon, Modal, PillBadge } from '@platform/components';
import { ReactNode, useContext, useState } from 'react';
import BusinessChallengeStep from './BusinessChallengeStep';
import SupportTypeStep from './SupportTypesStep';
import SkillsStep from './SkillsStep';
import { HeaderFooterLayout } from '../app';
import FinalDetailsStep from './FinalDetailsStep';
import { navigate } from 'wouter/use-browser-location';
import dataTestids from '../constants/test-ids';

const CircleCheckIcon = () => {
  return (
    <div className='flex items-center justify-center bg-gray-500 rounded-full w-6 h-6'>
      <Icon type='check' size='22' stroke='white' />
    </div>
  );
};

const STEP_LABELS = ['Business Challenge', 'Skills', 'Support Type', 'Final Details'] as const;

const OutlineProgressSteps = () => {
  const { stepId: currentStep } = useParams();

  return (
    <div className='font-header font-semibold flex'>
      {STEP_LABELS.map((stepName, i) => {
        const isCurrentStep = currentStep === (i + 1).toString();
        const isPreviousStep = i <= parseInt(currentStep ?? '') - 2;
        return (
          <div
            key={stepName}
            className={cx(
              'flex items-center',
              !isCurrentStep && !isPreviousStep ? 'text-gray-300' : 'text-gray-500',
            )}
          >
            {i > 0 && <div className='mx-4 w-8 h-0.5 bg-gray-200' />}
            {!isPreviousStep && (
              <div
                className={cx(
                  'w-6 h-6 rounded-full border-2 flex items-center justify-center',
                  isCurrentStep ? 'border-gray-500' : 'border-gray-300',
                )}
              >
                {i + 1}
              </div>
            )}
            {isPreviousStep && <CircleCheckIcon />}
            <div className='ml-2'>{stepName}</div>
          </div>
        );
      })}
    </div>
  );
};

const CompletedStep = ({
  stepName,
  stepNumber,
  editClicked,
  children,
  dataTestId,
}: {
  stepName: string;
  stepNumber: number;
  editClicked: (stepNumber: number) => void;
  dataTestId: string;
  children: ReactNode;
}) => {
  return (
    <div className='flex bg-white p-2 gap-2 items-center' data-testid={dataTestId}>
      <CircleCheckIcon />
      <span className='font-bold'>{stepName}:</span>
      {children}
      <div className='ml-auto'>
        <Button
          onPress={() => editClicked(stepNumber)}
          size='lg'
          variant='link-color'
          tailIcon='edit-01'
        >
          Edit
        </Button>
      </div>
    </div>
  );
};

const CompletedSteps = () => {
  const { stepId: currentStepParam } = useParams();
  const currentStep = parseInt(currentStepParam ?? '');
  const createProjectCtx = useContext(CreateProjectContext);
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [stepEditClicked, setStepEditClicked] = useState<number | null>(null);

  const editClicked = (stepNumber: number) => {
    setStepEditClicked(stepNumber);
    setShowAreYouSureModal(true);
  };

  const closeModal = () => {
    setStepEditClicked(null);
    setShowAreYouSureModal(false);
  };

  /* Resets values on the CreateProjectContext so that only values for steps
   * up-to-and-including the target step are set, with subsequent step values rest to
   * unset/null */
  const resetContextToStep = (targetStep: number) => {
    // Nothing to do if target step is 3, as that is the last step with context values;
    // Step 4 doesn't set on context as it's the last step
    if (targetStep >= 3) {
      return;
    }
    if (targetStep < 3) {
      createProjectCtx.setSupportTypes(new Set([]));
    }
    if (targetStep < 2) {
      createProjectCtx.setSkills(new Set([]));
    }
    if (targetStep < 1) {
      createProjectCtx.setBusinessChallenge('');
    }
  };

  return (
    <>
      <Modal
        isOpen={showAreYouSureModal}
        variant='dialog'
        onClose={() => closeModal()}
        headerDivision='divider'
        title='Are you sure?'
        primaryCTA={{
          text: 'Edit previous step',
          buttonVariant: 'primary',
          callback: () => {
            setShowAreYouSureModal(false);
            resetContextToStep(stepEditClicked ?? 1);
            navigate(`platform${ROUTES.CREATE_PROJECT}/${stepEditClicked}`);
          },
        }}
        secondaryCTA={{
          text: 'Cancel',
          buttonVariant: 'secondary-color',
          callback: () => closeModal(),
        }}
        ignoreExtraPadding
      >
        <span>
          Editing a previous step will reset all subsequent steps. Are you sure you want to
          continue?
        </span>
      </Modal>
      <div className='flex flex-col gap-2 w-full'>
        {currentStep > 1 && (
          <CompletedStep
            editClicked={editClicked}
            stepName={STEP_LABELS[0]}
            stepNumber={1}
            dataTestId={dataTestids.CREATE_PROJECT.COMPLETED_STEP_BUSINESS_CHALLENGE}
          >
            {createProjectCtx.businessChallenge}
          </CompletedStep>
        )}
        {currentStep > 2 && (
          <CompletedStep
            editClicked={editClicked}
            stepName={STEP_LABELS[1]}
            stepNumber={2}
            dataTestId={dataTestids.CREATE_PROJECT.COMPLETED_STEP_SKILLS}
          >
            {Array.from(createProjectCtx.skills).map((skill) => (
              <PillBadge color='gray' key={skill} size='lg' variant='plain'>
                {skill}
              </PillBadge>
            ))}
          </CompletedStep>
        )}
        {currentStep > 3 && (
          <CompletedStep
            editClicked={editClicked}
            stepName={STEP_LABELS[2]}
            stepNumber={3}
            dataTestId={dataTestids.CREATE_PROJECT.COMPLETED_STEP_SUPPORT_TYPES}
          >
            {/* TODO: Copy/pasted from above; replace this */}
            {Array.from(createProjectCtx.supportTypes).map((supporType) => (
              <PillBadge color='gray' key={supporType} size='lg' variant='plain'>
                {supporType}
              </PillBadge>
            ))}
          </CompletedStep>
        )}
      </div>
    </>
  );
};

// TODO: What to name these when editing?
const CreateProjectPage = () => {
  const { stepId: currentStep } = useParams();
  const [businessChallenge, setBusinessChallenge] = useState('');
  const [skills, setSkills] = useState<Set<SkillType>>(new Set([]));
  const [supportTypes, setSupportTypes] = useState<Set<SupportType>>(new Set([] as SupportType[]));

  return (
    <HeaderFooterLayout useDashboardHeader>
      <CreateProjectContext.Provider
        value={{
          businessChallenge,
          setBusinessChallenge,
          skills,
          setSkills,
          supportTypes,
          setSupportTypes,
        }}
      >
        <div className='w-full max-w-[1280px] sm:-mt-12 sm:py-12 bg-gray-50 flex flex-col items-center gap-8'>
          <OutlineProgressSteps />
          <CompletedSteps />
          <div className='w-full flex flex-col gap-4 items-center justify-center'>
            {/* Page header */}
            <div className='flex text-4xl font-header font-semibold items-center'>
              <div
                className={cx(
                  'w-12 h-12 rounded-full border-4 border-gray-500 text-gray-500 flex items-center justify-center',
                )}
              >
                {currentStep}
              </div>
              <h1
                className='ml-2 text-gray-500'
                data-testid={dataTestids.CREATE_PROJECT.STEP_HEADER}
              >
                {STEP_LABELS[parseInt(currentStep ?? '1') - 1]}
              </h1>
            </div>
            {currentStep === '1' && <BusinessChallengeStep />}
            {currentStep === '2' && <SkillsStep />}
            {currentStep === '3' && <SupportTypeStep />}
            {currentStep === '4' && <FinalDetailsStep />}
          </div>
        </div>
      </CreateProjectContext.Provider>
    </HeaderFooterLayout>
  );
};

export default CreateProjectPage;
